import {Injectable,Injector} from '@angular/core';
import {AngularFireFunctions} from '@angular/fire/functions';
import {Observable,Subscription,forkJoin,of,timer} from 'rxjs';
import {switchMap,take,timeout} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {PublishedResourceRepository} from './repository/PublishedResourceRepository';
import {WaihonaUserRepository} from './repository/WaihonaUserRepository';
import {OrganizationRepository} from './repository/OrganizationRepository';
import {CollectionRepository} from './repository/CollectionRepository';
import {FrameworkRepository} from './repository/FrameworkRepository';
import {BadgesRepository} from './repository/BadgesRepository';

export type CatalogEntity = 'resources'|'users'|'organizations'|'collections'|'frameworks'|'badges';
export interface CatalogRequest {text?:string;filters?:Array<{category:string;value:string;keys?:string[]}>;exactMatch?:boolean;}
interface CatalogResponse {guids:string[];total:number;hasMore:boolean;offset:number;nextOffset?:number;}
@Injectable({providedIn:'root'})
export class ElasticCatalogService {
    public readonly enabled = (environment as any).search?.enabled === true;
    constructor(private functions:AngularFireFunctions,private injector:Injector) {}
    public page$<T>(entity:CatalogEntity,request:CatalogRequest,offset:number):Observable<{items:T[];response:CatalogResponse}> {
        const config = (environment as any).search;
        if (!this.enabled || config.sourceProject !== environment.firebase.projectId || !['dev-waihona','pubstg-waihona'].includes(config.sourceProject)) {
            throw new Error('Search is not configured for this build.');
        }
        const repositories = {resources:PublishedResourceRepository,users:WaihonaUserRepository,organizations:OrganizationRepository,
            collections:CollectionRepository,frameworks:FrameworkRepository,badges:BadgesRepository};
        const repo:any = this.injector.get(repositories[entity] as any);
        return this.functions.httpsCallable(config.callable)({...request,entity,offset,size:24}).pipe(timeout(20000),take(1),
            switchMap((response:CatalogResponse) => {
                // Hydrate only the returned page from current Firestore records: preserve domain methods,
                // live attribution and Firestore permissions; never render stale indexed private data.
                const records:Observable<T[]> = !response.guids.length ? of([] as T[]) : entity !== 'collections' ? repo.getPageFromServer$(response.guids) :
                    forkJoin(response.guids.map(id=>repo.get$(id,false).pipe(take(1)) as Observable<T>));
                return records.pipe(
                    switchMap((items:T[]) => of({items:items.filter(Boolean),response})));
            }));
    }
    public session<T>(entity:CatalogEntity,onItems:(items:T[])=>void):ElasticCatalogPager<T> {
        return new ElasticCatalogPager<T>(this,entity,onItems);
    }
}
/** One session per view. New queries cancel in-flight responses; page requests cannot overlap. */
export class ElasticCatalogPager<T> extends Subscription {
    public loading = false;
    public error = '';
    public total = 0;
    public hasMore = false;
    public items:T[] = [];
    private offset = 0;
    private request:CatalogRequest = {};
    private active:Subscription;
    private generation = 0;
    private signature:string;
    constructor(private api:ElasticCatalogService,private entity:CatalogEntity,private onItems:(items:T[])=>void) {
        super();this.add(() => {this.generation++;if(this.active) {this.active.unsubscribe();}});
    }
    public refresh(request:CatalogRequest = {},force=false):void {
        const signature = JSON.stringify(request);
        if (this.closed || (!force && signature === this.signature)) { return; }
        this.signature=signature;this.request=JSON.parse(signature);this.generation++;
        if(this.active) {this.active.unsubscribe();}
        this.items=[];this.offset=0;this.total=0;this.error='';this.hasMore=true;this.loading=false;this.onItems([]);this.next();
    }
    public next():void {
        if(this.closed || this.loading || !this.hasMore) {return;}
        this.loading=true;this.error='';const generation=this.generation;
        this.active=timer(150).pipe(switchMap(()=>this.api.page$<T>(this.entity,this.request,this.offset))).subscribe({
            next:({items,response})=>{
                if(this.closed || generation!==this.generation) {return;}
                const seen=new Set(this.items.map((x:any)=>x.guid));
                this.items=[...this.items,...items.filter((x:any)=>{if(seen.has(x.guid)) {return false;}seen.add(x.guid);return true;})];
                this.offset=response.nextOffset ?? response.offset+response.guids.length;this.total=response.total;this.hasMore=response.hasMore;this.loading=false;this.onItems(this.items);
            },
            error:()=>{if(!this.closed && generation===this.generation) {this.loading=false;this.error='Search is temporarily unavailable. Please retry.';}}
        });
    }
    public retry():void {this.next();}
}
